import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { images, LoadingSpinner } from 'yankees-react-utils';
import { useOktaQuery, HeatMaps } from '../../../data';
import { base64ToBlob } from '../../../utils/heatmaps';
import ReportCard from '../../../components/ReportCard';
import StrikeZone from '../../../components/StrikeZone';

const StyledPitchGroupBox = styled.div`
    display: flex;
    justify-content: center;
    background-repeat: no-repeat;
    background-size: 150px;
    background-size: contain;
    padding: 0 50px;
    ${({ $islhb }) => ($islhb ? `
        background-position: 0 0;
        background-image: url(${images.lhbImgFront});
    ` : `
        background-position: right 0;
        background-image: url(${images.rhbImgFront});
    `)}
    p {
        font-size: 1.5em;
        font-weight: bold;
        text-align: center;
        background: #C7C7C7 !important;
        -webkit-print-color-adjust: exact; 
        padding: 5px;
        border-bottom: 2px solid #000;
        margin: 0;
    }
`;

const MatchupReportGenericHandednessHeatmaps = ({ pastYearsToUse, batterId, isLHB, isLHP }) => {
    const { t: translate } = useTranslation();
    const { data: heatmaps, isFetching: isHeatmapsLoading } = useOktaQuery(
        ['heatmaps', batterId],
        (t) => HeatMaps.getBatterHeatMapsByPitchType(t, batterId, { yearid: (pastYearsToUse * -1) }),
    );

    const getHeatmapForSituation = (subtype, split) => {
        if (heatmaps) {
            const matchingHeatmap = heatmaps.find((x) => x.IsLHP === isLHP && x.IsLHB === isLHB && x.SubType === subtype && x.Split === split);
            if (matchingHeatmap) {
                return base64ToBlob(matchingHeatmap);
            }
        }
        return null;
    };

    return (
        <>
            <ReportCard title={<>{translate('sections.opponent.player.attack-counts')} <small>(0-0, 1-0, 1-1, 0-1, 2-0, 2-1, 3-0, 3-1)</small></>}>
                <StyledPitchGroupBox $islhb={isLHB}>
                    {isHeatmapsLoading && <LoadingSpinner />}
                    {!isHeatmapsLoading && (
                        <>
                            <div key="fbat">
                                <p>{translate('baseball.pitches.fastball_one')}</p>
                                <StrikeZone
                                    enableZoom={false}
                                    seriesData={[]}
                                    size="sm"
                                    height={250}
                                    showoffgrid
                                    heatMapBackground={getHeatmapForSituation('fb', 'Early')}
                                    hideGrid
                                    showHeatMapBackground
                                    pitcherView
                                    showStrikeZone={false}
                                    showbatter={false}
                                />
                            </div>
                            <div key="chat">
                                <p>{translate('baseball.pitches.changeup_one')}</p>
                                <StrikeZone
                                    enableZoom={false}
                                    seriesData={[]}
                                    size="sm"
                                    height={250}
                                    showoffgrid
                                    heatMapBackground={getHeatmapForSituation('ch', 'Early')}
                                    hideGrid
                                    showHeatMapBackground
                                    pitcherView
                                    showStrikeZone={false}
                                    showbatter={false}
                                />
                            </div>
                            <div key="bbat">
                                <p>{translate('baseball.pitches.breaking-ball_one')}</p>
                                <StrikeZone
                                    enableZoom={false}
                                    seriesData={[]}
                                    size="sm"
                                    height={250}
                                    showoffgrid
                                    heatMapBackground={getHeatmapForSituation('bb', 'Early')}
                                    hideGrid
                                    showHeatMapBackground
                                    pitcherView
                                    showStrikeZone={false}
                                    showbatter={false}
                                />
                            </div>
                        </>
                    )}
                </StyledPitchGroupBox>
            </ReportCard>
            <ReportCard title={<>2-{translate('baseball.strikes_one')} {translate('baseball.count')} <small>(0-2, 1-2, 2-2, 3-2)</small></>}>
                <StyledPitchGroupBox $islhb={isLHB}>
                    {isHeatmapsLoading && <LoadingSpinner />}
                    {!isHeatmapsLoading && (
                        <>
                            <div key="fb2s">
                                <p>{translate('baseball.pitches.fastball_one')}</p>
                                <StrikeZone
                                    enableZoom={false}
                                    seriesData={[]}
                                    size="sm"
                                    height={250}
                                    showoffgrid
                                    heatMapBackground={getHeatmapForSituation('fb', 'TwoStrikes')}
                                    hideGrid
                                    showHeatMapBackground
                                    pitcherView
                                    showStrikeZone={false}
                                    showbatter={false}
                                />
                            </div>
                            <div key="ch2s">
                                <p>{translate('baseball.pitches.changeup_one')}</p>
                                <StrikeZone
                                    enableZoom={false}
                                    seriesData={[]}
                                    size="sm"
                                    height={250}
                                    showoffgrid
                                    heatMapBackground={getHeatmapForSituation('ch', 'TwoStrikes')}
                                    hideGrid
                                    showHeatMapBackground
                                    pitcherView
                                    showStrikeZone={false}
                                    showbatter={false}
                                />
                            </div>
                            <div key="bb2s">
                                <p>{translate('baseball.pitches.breaking-ball_one')}</p>
                                <StrikeZone
                                    enableZoom={false}
                                    seriesData={[]}
                                    size="sm"
                                    height={250}
                                    showoffgrid
                                    heatMapBackground={getHeatmapForSituation('bb', 'TwoStrikes')}
                                    hideGrid
                                    showHeatMapBackground
                                    pitcherView
                                    showStrikeZone={false}
                                    showbatter={false}
                                />
                            </div>
                        </>
                    )}
                </StyledPitchGroupBox>
            </ReportCard>
        </>
    );
};

MatchupReportGenericHandednessHeatmaps.propTypes = {
    pastYearsToUse: PropTypes.number.isRequired,
    batterId: PropTypes.number.isRequired,
    isLHB: PropTypes.bool.isRequired,
    isLHP: PropTypes.bool.isRequired,
};

export default MatchupReportGenericHandednessHeatmaps;
